import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./HrNewRequestDrawer.module.css";
import Informator from "../../components/common/Informator/Informator";
import {
    FiX,
    FiArrowLeft,
    FiChevronRight,
    FiCheck,
    FiSend,
    FiUpload,
    FiRotateCcw,
    FiLoader,
    FiInbox,
    FiSearch,
    FiBriefcase,
    FiCpu,
    FiAlertCircle,
    FiUsers
} from "react-icons/fi";
import { FIELD_TYPE, WITHDRAWAL_TYPE_CODE } from "../../constants/hrRequestTypes";
import hrTypeResolver from "../../services/hrTypeResolver";
import hrConfigService from "../../services/hrConfigService";
import { DatePicker, TimePicker } from "../../components/common/DatePicker";
// [2026-09-29] Para dat OD–DO (DO wyrównuje się do OD) i granica 5 dni wstecz dla pól okresu — reguły wspólne z serwerem.
import { poZmianiePola, nazwaPolaOd, minDlaDo } from "../../utils/paraDat";
import { minPolaDaty } from "../../utils/granicaWniosku";
import { DepartmentMemberPicker } from "../../components/common/DepartmentMemberPicker";
import { czyPoleZastepstwa } from "../../utils/hrBrakOsoby";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../hooks/useAuth";
import hrDataService from "../../services/hrDataService";
import { apiService } from "../../api/apiService";
import { getStatusInfo as getStatusInfoFn } from "../../constants/hrRequestTypes";
import { nudgeDrawer } from "../../utils/modalAttention";
import { czyTypZarzadczy } from "../../utils/hrTypZarzadczy";
import { etykietaTypuWniosku } from "../../utils/hrEtykiety";
import { renderFormField, isFieldVisible, bezUkrytychPol } from "../../utils/hrFormFieldRenderer";
/* [polecenie właściciela 2026-09-28] Lista „co mogę złożyć” wydzielona do haka — ta sama dla /wnioski-hr i dla karty
   „Wnioski pracownicze” w oknie „Utwórz nowe zgłoszenie”. Opis ścieżki akceptacji i wartości w podsumowaniu okna
   liczy czysty moduł (zmierzony bez montowania). */
import { useDostepneTypyWnioskow } from "../../hooks/useDostepneTypyWnioskow";
import { KTO_AKCEPTUJE_PRZED_ZLOZENIEM, wartoscPolaDoPodsumowania, odmiana, opisKategoriiWnioskow, opisTypuWniosku } from "../../utils/hrOpisTypuWniosku";
import LadowanieStrony from "../../components/common/Ladowanie/LadowanieStrony";

const TYPY_ROZLICZEN = new Set([FIELD_TYPE.REPEATER, FIELD_TYPE.MONEY, FIELD_TYPE.DATETIME]);

// ── Work-days calculator (skip weekends) ─────────────────────────
function calcWorkDays(from, to) {
    if (!from || !to) return 0;
    const d1 = new Date(from + "T00:00:00");
    const d2 = new Date(to + "T00:00:00");
    if (isNaN(d1.getTime()) || isNaN(d2.getTime()) || d2 < d1) return 0;
    let count = 0;
    const cur = new Date(d1);
    while (cur <= d2) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6) count++;
        cur.setDate(cur.getDate() + 1);
    }
    return count;
}

// ── Animations ───────────────────────────────────────────────────
const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.15 } },
};

const drawerVariants = {
    hidden: { x: "100%" },
    visible: { x: 0, transition: { type: "spring", damping: 28, stiffness: 300 } },
    exit: { x: "100%", transition: { duration: 0.2, ease: "easeIn" } },
};

const slideVariants = {
    enter: { opacity: 0, x: 30 },
    center: { opacity: 1, x: 0, transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] } },
    exit: { opacity: 0, x: -30, transition: { duration: 0.15 } },
};

const STEP_LABELS = ["Kategoria", "Typ wniosku", "Formularz"];
// [okno 2026-09-28] Rama „okno” (karta „Wnioski pracownicze” w „Utwórz nowe zgłoszenie”) ma jeszcze podsumowanie.
const KROKI_OKNA = ["Kategoria", "Typ wniosku", "Formularz", "Podsumowanie"];

/** Komunikat błędu POD polem wymaganym (okno „Utwórz nowe zgłoszenie”) — mówi, CO zrobić, a nie tylko „błąd”. */
const komunikatPolaWymaganego = (f) => {
    const nazwa = f.label || f.name;
    if (f.type === FIELD_TYPE.CHECKBOX) return `Zaznacz „${nazwa}”, aby złożyć wniosek.`;
    if (f.type === FIELD_TYPE.REPEATER) return `Dodaj co najmniej jedną pozycję w „${nazwa}”.`;
    if (f.type === FIELD_TYPE.FILE) return `Dołącz plik: „${nazwa}”.`;
    return `Pole „${nazwa}” jest wymagane.`;
};

// [R22, poprawka po przeglądzie 2026-09-13] Pola TIME kreatora stoją na TimePickerze aplikacji (import wyżej).
// Stał tu własny picker z dwóch list: minuty tylko 00/15/30/45, więc zapisane lub potrzebne 08:07 nie dało się
// ani pokazać, ani wybrać (wyjścia prywatne i służbowe, odbiór i odpracowanie czasu). Wartość bez zmian: "HH:mm".

// ── Main Component ───────────────────────────────────────────────
// `typDomyslny` — [brief właściciela 2026-08-31, pkt 4.3c] typ wskazany kafelkiem sekcji
// „Złóż wniosek". Kreator otwiera się wtedy od razu na formularzu: kafelek z nazwą wniosku,
// po którym trzeba wybrać tę samą nazwę jeszcze raz, byłby pustym gestem.
const HrNewRequestDrawer = ({
    isOpen,
    onClose,
    onRequestCreated,
    withdrawalTarget,
    typDomyslny = null,
    /**
     * [zgłoszenie właściciela 2026-09-02] TEN KREATOR SKŁADA WYŁĄCZNIE WNIOSKI O SOBIE. Gdy mimo to
     * trafi tu typ zarządczy (kafelek, skrót, stary link), ekran-gospodarz może go PRZEJĄĆ: dostaje
     * kod typu i otwiera kreator „w imieniu pracownika", który zaczyna od wyboru osoby. Bez tego
     * propsa kreator własny mówi wprost, że tu tego wniosku nie złoży — i nie pokazuje przycisku.
     */
    onWniosekZarzadczy = null,
    /**
     * [polecenie właściciela 2026-09-28] „w oknie Utworz nowe zgloszenie - zrob 2 karty […] wnioski pracownicze […]
     * i caly proces uzupelnienia wniosku, pamietaj zeby byla zgodnosc okien modal itp”.
     * OPRAWA: „szuflada” (domyślnie — /wnioski-hr i Mój Dział działają DOKŁADNIE jak dotąd) albo „okno”: TEN SAM
     * kreator (walidacja, prefill, salda, spółka, wycofanie, zapadka typu zarządczego, wysyłka) osadzony w karcie
     * okna zgłoszenia, bez własnego tła i bez własnego ✕ — zamyka go jeden Modal gospodarza.
     *  - `klasyOkna`   klasy ramy okna gospodarza (nagłówek, treść, stopka, przyciski), żeby wniosek wyglądał jak zgłoszenie;
     *  - `onWstecz`    „Wróć do listy” — gospodarz wraca do listy typów (i sam pyta, czy porzucić wpisane dane);
     *  - `onWypelnienie(bool)` — czy człowiek coś już wpisał (gospodarz pyta przed porzuceniem przez ✕ / Anuluj);
     *  - `dostepneTypy` — lista już pobrana przez gospodarza (ten sam hak), żeby nie pytać serwera drugi raz.
     */
    oprawa = "szuflada",
    klasyOkna = null,
    onWstecz = null,
    onWypelnienie = null,
    dostepneTypy = null,
}) => {
    const [step, setStep] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [formData, setFormData] = useState({});
    const [sensitiveHints, setSensitiveHints] = useState({}); // klucz pola -> zamaskowana wartosc zapisana w systemie
    const [submitting, setSubmitting] = useState(false);
    // [okno] Krok ramy okna (formularz → podsumowanie), błędy pod polami po „Dalej” i znacznik „człowiek coś wpisał”.
    // W szufladzie żaden z nich nic nie zmienia: `pokazBledyPol` zostaje false, podsumowania nie ma.
    const wOknie = oprawa === "okno";
    const [krokOkna, setKrokOkna] = useState("formularz");
    const [pokazBledyPol, setPokazBledyPol] = useState(false);
    const [probaDalej, setProbaDalej] = useState(0);
    const [edytowano, setEdytowano] = useState(false);
    const oknoRef = useRef(null);

    // Withdrawal states
    const [eligibleRequests, setEligibleRequests] = useState([]);
    const [eligibleLoading, setEligibleLoading] = useState(false);
    const [selectedOriginal, setSelectedOriginal] = useState(null);
    const { showToast } = useToast();
    const { user } = useAuth();

    // Load real balances from hrDataService
    const [balances, setBalances] = useState({});
    useEffect(() => {
        if (!user?.id || !isOpen) { setBalances({}); return; }
        const loadBalances = async () => {
            const result = await hrDataService.getBalances(user.id);
            setBalances(result);
        };
        loadBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, isOpen]);

    // ── Lista wniosków: TYLKO TO, CO MI PRZYSŁUGUJE (brief 2026-08-30, pkt 6) ──────────
    // BYŁO ŹLE: kreator brał komplet typów z konfiguracji i zawężał listę w przeglądarce —
    // po formie zatrudnienia i wymiarze etatu, po to tylko, żeby typ WYSZARZYĆ. Sald,
    // jednorazowości ani rangi managerskiej nie sprawdzał wcale, a wyszukiwarka omijała
    // nawet to zawężenie, bo szukała po pełnej liście (audyt M-12).
    // TERAZ: serwer przysyła wyłącznie typy, które ten pracownik może złożyć, pogrupowane
    // w kategorie i bez kategorii pustych — nie ma czego chować ani czym obchodzić.
    // [2026-09-28] Pobranie i zawężenie listy żyje w haku `useDostepneTypyWnioskow` (ta sama reguła dla okna
    // „Utwórz nowe zgłoszenie”). Gospodarz, który listę już ma, podaje ją w `dostepneTypy` — wtedy bez 2. zapytania.
    const wlasneTypy = useDostepneTypyWnioskow(isOpen && !dostepneTypy);
    const { categories, allTypes, typesError } = dostepneTypy || wlasneTypy;
    const [typeSearch, setTypeSearch] = useState("");    // zapytanie wyszukiwarki w kreatorze

    // ── Spółka-adresat wniosku (brief 2026-08-30, pkt 3) ──────────────────────────────
    // „Jak składam wniosek, a mam 2-3 spółki, to mam WYBRAĆ FIRMĘ, do której składam."
    // Jedna spółka → nie pytamy (pokazujemy tylko, dokąd wniosek idzie). Wybór jedzie
    // w `companyId`; backend go waliduje i zamraża w snapshocie razem z logo spółki.
    const [companies, setCompanies] = useState([]);
    const [companyId, setCompanyId] = useState(null);
    useEffect(() => {
        if (!isOpen) { setCompanies([]); setCompanyId(null); return undefined; }
        let cancelled = false;
        (async () => {
            const lista = await hrConfigService.getMyCompanies();
            if (cancelled) return;
            setCompanies(lista);
            setCompanyId(lista.length === 1 ? lista[0].id : (lista.find((c) => c.isPrimary)?.id ?? null));
        })();
        return () => { cancelled = true; };
    }, [isOpen]);
    const mustPickCompany = companies.length > 1;
    const activeCompany = companies.find((c) => c.id === companyId) || null;

    // Balance pool info cache
    const [balancePoolCache, setBalancePoolCache] = useState({});
    const getBalancePoolCached = useCallback(async (poolId) => {
        if (!poolId) return null;
        if (balancePoolCache[poolId]) return balancePoolCache[poolId];
        const info = await hrTypeResolver.getBalancePool(poolId);
        if (info) setBalancePoolCache(prev => ({ ...prev, [poolId]: info }));
        return info;
    }, [balancePoolCache]);

    // Load balance pool info for active type
    const [activeBalancePoolInfo, setActiveBalancePoolInfo] = useState(null);

    const reset = useCallback(() => {
        setStep(0);
        setTypeSearch("");
        setSelectedCategory(null);
        setSelectedType(null);
        setFormData({});
        setActiveBalancePoolInfo(null);
        setEligibleRequests([]);
        setSelectedOriginal(null);
        setEligibleLoading(false);
        setKrokOkna("formularz");
        setPokazBledyPol(false);
        setEdytowano(false);
    }, []);

    const handleClose = useCallback(() => {
        onClose();
        setTimeout(reset, 300);
    }, [onClose, reset]);

    const handleCategorySelect = useCallback((catId) => {
        setSelectedCategory(catId);
        setStep(1);
    }, []);

    const handleTypeSelect = useCallback((typeId) => {
        setSelectedType(typeId);
        setFormData({});
        setSensitiveHints({});
        setStep(2);
    }, []);

    // Preselekcja z kafelka. Wykonuje się RAZ na otwarcie — bez zapadki każde odświeżenie listy
    // kategorii cofałoby wypełniony już formularz na sam początek.
    const wczytanyTypDomyslny = useRef(null);
    useEffect(() => {
        if (!isOpen) { wczytanyTypDomyslny.current = null; return; }
        if (!typDomyslny || wczytanyTypDomyslny.current === typDomyslny) return;
        wczytanyTypDomyslny.current = typDomyslny;
        setSelectedType(typDomyslny);
        setFormData({});
        setSensitiveHints({});
        setStep(2);
    }, [isOpen, typDomyslny]);

    const handleBack = useCallback(() => {
        if (step === 2) {
            // Wejście „z kafelka" pomija krok kategorii, więc kategoria jest pusta — cofanie do listy
            // typów wyprowadziłoby na PUSTY ekran. Wracamy wtedy do wyboru kategorii.
            setStep(selectedCategory ? 1 : 0);
            setSelectedType(null); setFormData({}); setSensitiveHints({});
        }
        else if (step === 1) { setStep(0); setSelectedCategory(null); }
    }, [step, selectedCategory]);

    // Typ wniosku potrzebny regule pary dat, a `activeType` jest zadeklarowany niżej — ref aktualizowany efektem po deklaracji.
    const activeTypeRef = useRef(null);
    const handleFieldChange = useCallback((name, value) => {
        // [2026-09-29] Zmiana daty OD późniejszej niż DO przesuwa DO na OD (utils/paraDat) — bez sprzecznego zakresu na ekranie.
        setFormData((prev) => poZmianiePola(prev, name, value, activeTypeRef.current?.fields || null));
        setEdytowano(true);
    }, []);

    // [okno] „Wypełniony” = człowiek coś wpisał albo wybrał (prefill z biblioteki danych i pola liczone przez system
    // się nie liczą). Gospodarz na tej podstawie pyta przed porzuceniem formularza przez ✕ / Anuluj / „Wróć do listy”.
    useEffect(() => {
        if (typeof onWypelnienie === "function") onWypelnienie(edytowano);
    }, [edytowano, onWypelnienie]);

    // [okno] Po „Dalej” z brakami fokus trafia na PIERWSZE pole z błędem — człowiek nie szuka, co poprawić.
    useEffect(() => {
        if (!probaDalej || !oknoRef.current) return;
        const pole = oknoRef.current.querySelector(
            "[data-pole-z-bledem] input, [data-pole-z-bledem] select, [data-pole-z-bledem] textarea, [data-pole-z-bledem] button");
        if (pole && typeof pole.focus === "function") { pole.focus(); return; }
        const alert = oknoRef.current.querySelector("[role='alert']");
        if (alert && typeof alert.scrollIntoView === "function") alert.scrollIntoView({ block: "center" });
    }, [probaDalej]);

    // ── Withdrawal: select original request to withdraw ──────────
    const handleSelectOriginal = useCallback((req) => {
        setSelectedOriginal(req);
        const fd = req.formData || {};
        const dateFrom = fd.dateFrom || fd.date;
        const dateTo = fd.dateTo || dateFrom;
        const dateRange = dateFrom && dateTo
            ? `${new Date(dateFrom + "T00:00:00").toLocaleDateString("pl-PL")} – ${new Date(dateTo + "T00:00:00").toLocaleDateString("pl-PL")}`
            : dateFrom ? new Date(dateFrom + "T00:00:00").toLocaleDateString("pl-PL") : "—";
        setFormData({
            originalRequestId: req.id,
            originalRequestNumber: req.requestNumber,
            originalTypeLabel: req._typeLabel || etykietaTypuWniosku(req),
            originalDateRange: dateRange,
            reason: "",
        });
    }, []);

    // Active request type definition (from resolver)
    const [activeType, setActiveType] = useState(null);
    useEffect(() => { activeTypeRef.current = activeType; }, [activeType]);
    useEffect(() => {
        if (!selectedType) { setActiveType(null); return; }
        const loadType = async () => {
            // Najpierw pozycja z listy DOSTĘPNYCH (ta sama, którą pracownik przed chwilą kliknął),
            // a dopiero gdy jej nie ma — skrót „wycofaj wniosek", który wchodzi tu z zewnątrz.
            const type = allTypes.find((t) => t.id === selectedType || t.code === selectedType)
                || await hrTypeResolver.getRequestType(selectedType);
            setActiveType(type);
            // Load balance pool info for this type
            if (type?.limitPool) {
                const info = await getBalancePoolCached(type.limitPool);
                setActiveBalancePoolInfo(info);
            } else {
                setActiveBalancePoolInfo(null);
            }
        };
        loadType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType, allTypes]);

    // ── ZAPADKA: typ zarządczy w kreatorze własnym ────────────────────────────────────────────
    // Lista jest już zawężona, ale typ potrafi wejść bokiem (`typDomyslny` z kafelka lub skrótu).
    // Wtedy: gospodarz przejmuje (→ kreator „w imieniu pracownika" z krokiem wyboru osoby) albo,
    // gdy gospodarza nie ma, formularz się NIE renderuje i przycisku „Złóż" nie ma. Nigdy nie
    // składamy wniosku o pracowniku bez wskazania pracownika.
    const typZarzadczyWybrany = czyTypZarzadczy(activeType);
    const przekazanyTyp = useRef(null);
    useEffect(() => {
        if (!isOpen) { przekazanyTyp.current = null; return; }
        if (!typZarzadczyWybrany || !onWniosekZarzadczy) return;
        const kod = activeType.code || selectedType;
        if (przekazanyTyp.current === kod) return;   // raz na otwarcie — bez pętli z gospodarzem
        przekazanyTyp.current = kod;
        onWniosekZarzadczy(kod);
        handleClose();
    }, [isOpen, typZarzadczyWybrany, onWniosekZarzadczy, activeType, selectedType, handleClose]);

    // Prefill z biblioteki danych pracownika: pola zwykle wczytujemy uzupelnione, wrazliwe pokazujemy
    // jako podpowiedz (zapisano ...) i wymagamy ponownego wpisania. Tylko wnioski o siebie (bez wskazania pracownika).
    useEffect(() => {
        if (!activeType || !user?.id || !isOpen) return;
        if (activeType.requiresTargetEmployee) { setSensitiveHints({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await apiService.getHrFieldPrefill(user.id);
                if (cancelled) return;
                const data = res?.data || {};
                const fieldNames = new Set((activeType.fields || []).map((f) => f.name));
                const values = data.values || {};
                setFormData((prev) => {
                    const next = { ...prev };
                    Object.entries(values).forEach(([k, v]) => {
                        if (fieldNames.has(k) && (next[k] === undefined || next[k] === null || next[k] === "")) next[k] = v;
                    });
                    return next;
                });
                const hints = {};
                Object.entries(data.sensitive || {}).forEach(([k, meta]) => {
                    if (fieldNames.has(k) && meta?.masked) hints[k] = meta.masked;
                });
                setSensitiveHints(hints);
            } catch { /* prefill best-effort */ }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeType, user?.id, isOpen]);

    // Auto-compute workDays when dateFrom/dateTo change
    useEffect(() => {
        if (!activeType) return;
        const hasWorkDays = activeType.fields.some((f) => f.name === "workDays" && f.computed);
        if (!hasWorkDays) return;
        const wd = calcWorkDays(formData.dateFrom, formData.dateTo);
        if (wd !== formData.workDays) {
            setFormData((prev) => ({ ...prev, workDays: wd }));
        }
    }, [formData.dateFrom, formData.dateTo, activeType, formData.workDays]);

    const handleSubmit = useCallback(async () => {
        if (submitting || !user) return;
        // Druga zapadka, niezależna od renderu: nawet wywołany wprost nie złoży wniosku zarządczego.
        if (czyTypZarzadczy(activeType)) {
            showToast("Ten wniosek dotyczy pracownika — złóż go jako „Wniosek w imieniu pracownika”.", "error");
            return;
        }
        setSubmitting(true);

        try {
            // Zbierz pliki osobno (nie serializowane do localStorage)
            // [R24] Wartości pól UKRYTYCH warunkiem (showIf / showIfValue) nie idą na wniosek — np. pojazd wpisany przy
            // „samochód prywatny”, po przełączeniu na „kolej”. Załączniki też tylko z pól widocznych.
            const daneWidoczne = bezUkrytychPol(activeType?.fields || [], formData);
            const fileFields = activeType?.fields?.filter((f) => f.type === FIELD_TYPE.FILE) || [];
            const attachments = fileFields
                .map((f) => daneWidoczne[f.name])
                .filter(Boolean);

            // Oczyść formData z obiektów File (nie serializuj)
            const cleanFormData = { ...daneWidoczne };
            fileFields.forEach((f) => {
                if (cleanFormData[f.name] instanceof File) {
                    cleanFormData[f.name] = cleanFormData[f.name].name; // zachowaj nazwę
                }
            });

            const request = await hrDataService.createRequest(
                user, activeType?.code || selectedType, cleanFormData, attachments,
                // [brief pkt 3] Spółka-adresat idzie w DTO; przy jednej spółce to po prostu ona.
                { companyId }
            );

            await hrDataService.logInteraction("FORM_SUBMITTED", user, {
                typeId: activeType?.code || selectedType,
                requestNumber: request.requestNumber,
                fieldsCount: Object.keys(cleanFormData).length,
            }, request.id);

            showToast(
                `Wniosek ${request.requestNumber} został złożony pomyślnie`,
                "success"
            );

            if (onRequestCreated) onRequestCreated(request);
            // [okno] Okno NIE zamyka się po wysłaniu — gospodarz pokazuje potwierdzenie z linkiem do wniosku.
            if (wOknie) reset(); else handleClose();
        } catch (err) {
            // Wniosek utworzony, ale upload załączników padł — NIE traktuj jak porażki (inaczej user
            // składa ponownie => duplikat / blokada jednorazowego). Zamknij i tylko ostrzeż o załącznikach.
            if (err?.partial && err?.request) {
                if (onRequestCreated) onRequestCreated(err.request);
                showToast(err.message || "Wniosek złożony, ale załączniki nie doszły.", "warning");
                if (wOknie) reset(); else handleClose();
                return;
            }
            console.error("Błąd składania wniosku:", err);
            // Serwer odmawia po ludzku („Ten wniosek mogą składać wyłącznie przełożeni…"),
            // a `err.message` to techniczne „Request failed with status code 403".
            showToast(err?.response?.data?.error || err.message || "Błąd podczas składania wniosku", "error");
        } finally {
            setSubmitting(false);
        }
    }, [submitting, user, activeType, formData, selectedType, companyId, showToast, onRequestCreated, handleClose, wOknie, reset]);

    // Active category (resolved from the categories we already loaded)
    const activeCat = selectedCategory ? (categories || []).find(c => c.id === selectedCategory) || null : null;

    // Typy kategorii = to, co serwer już przysłał jako dostępne. Zero drugiego zapytania
    // i zero wyszarzania: wniosku, którego nie wolno złożyć, po prostu tu nie ma.
    const categoryTypes = useMemo(
        () => (categories || []).find((c) => c.id === selectedCategory)?.types || [],
        [categories, selectedCategory]
    );

    // ── Last request of this type ────────────────────────────────
    const [lastRequest, setLastRequest] = useState(null);
    useEffect(() => {
        if (!activeType?.code || !user?.id) { setLastRequest(null); return; }
        const loadLast = async () => {
            const result = await hrDataService.getLastRequestOfType(user.id, activeType.code);
            setLastRequest(result);
        };
        loadLast();
    }, [activeType?.code, user?.id]);

    // ── Withdrawal: load eligible requests when WITHDRAWAL type selected ──
    useEffect(() => {
        if (!activeType || activeType.code !== WITHDRAWAL_TYPE_CODE || !user?.id) {
            setEligibleRequests([]);
            setSelectedOriginal(null);
            return;
        }
        const loadEligible = async () => {
            setEligibleLoading(true);
            try {
                const eligible = await hrDataService.getEligibleForWithdrawal(user.id);
                for (const req of eligible) {
                    const type = await hrTypeResolver.getRequestType(req.typeId);
                    req._typeLabel = etykietaTypuWniosku(req, type);
                }
                setEligibleRequests(eligible);
            } catch { /* empty */ } finally { setEligibleLoading(false); }
        };
        loadEligible();
    }, [activeType, user?.id]);

    // ── Withdrawal: auto-navigate when withdrawalTarget is provided (shortcut) ──
    useEffect(() => {
        if (!withdrawalTarget || !isOpen) return;
        const initWithdrawal = async () => {
            const wType = await hrConfigService.ensureWithdrawalType();
            if (!wType) return;
            setSelectedType(wType.id || wType.code);
            setStep(2);
        };
        initWithdrawal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [withdrawalTarget, isOpen]);

    // ── Withdrawal: auto-select original from withdrawalTarget ──
    useEffect(() => {
        if (!withdrawalTarget || !activeType || activeType.code !== WITHDRAWAL_TYPE_CODE) return;
        // Resolve type label for the target
        const resolveAndSelect = async () => {
            const type = await hrTypeResolver.getRequestType(withdrawalTarget.typeId);
            const enriched = { ...withdrawalTarget, _typeLabel: type?.label || withdrawalTarget.typeId };
            handleSelectOriginal(enriched);
        };
        resolveAndSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [withdrawalTarget, activeType]);

    // ── Date validation (dateTo >= dateFrom) ─────────────────────
    const dateValidationError = useMemo(() => {
        if (!activeType) return null;
        const { dateFrom, dateTo } = formData;
        if (dateFrom && dateTo) {
            if (new Date(dateTo) < new Date(dateFrom)) {
                return "Data zakończenia nie może być wcześniejsza niż data rozpoczęcia";
            }
        }
        return null;
    }, [activeType, formData]);

    // ── Balance validation ────────────────────────────────────────
    const balanceInsufficient = useMemo(() => {
        if (!activeType?.limitPool) return false;
        const balance = balances[activeType.limitPool];
        if (!balance) return false;
        const remaining = balance.total - balance.used - balance.pending;
        const requestDays = formData.workDays || (formData.date ? 1 : 0);
        return requestDays > 0 && remaining - requestDays < 0;
    }, [activeType, balances, formData]);

    // Validate required fields (skip computed/readonly & showIf-hidden)
    // [okno 2026-09-28] Walidacja oddaje BŁĘDY (klucz pola → komunikat), a nie samo tak/nie: okno „Utwórz nowe
    // zgłoszenie” pokazuje je POD polami po „Dalej”. Reguła jest ta sama co dotąd — `isFormValid` = zero błędów,
    // więc szuflada (/wnioski-hr, Mój Dział) blokuje „Złóż wniosek” dokładnie w tych samych sytuacjach.
    const bledyFormularza = useMemo(() => {
        const bledy = {};
        if (!activeType) return bledy;

        // Special validation for WITHDRAWAL type
        if (activeType.code === WITHDRAWAL_TYPE_CODE) {
            if (!formData.originalRequestId) bledy.__wycofanie = "Wybierz z listy wniosek, który chcesz wycofać.";
            if (!formData.reason?.trim()) bledy.reason = "Podaj powód wycofania wniosku.";
            return bledy;
        }

        // Block if date validation fails
        if (dateValidationError) bledy.__daty = dateValidationError;
        // Block if insufficient balance
        if (balanceInsufficient) bledy.__saldo = "Niewystarczające saldo — skróć okres albo wybierz inny wniosek.";
        // [brief pkt 3] Kilka spółek = wskazanie spółki jest obowiązkowe; przy jednej nie pytamy.
        if (mustPickCompany && !companyId) bledy.__spolka = "Wybierz spółkę, do której składasz wniosek.";

        activeType.fields
            .filter((f) => {
                if (!f.required || f.type === FIELD_TYPE.INFO || f.computed) return false;
                // [R24] showIf + showIfValue (np. pola pojazdu tylko przy „samochód prywatny”) — pole ukryte nie blokuje złożenia.
                if (!isFieldVisible(f, formData)) return false;
                return true;
            })
            .forEach((f) => {
                const val = formData[f.name];
                let wypelnione;
                if (f.type === FIELD_TYPE.CHECKBOX) wypelnione = val === true;
                // [DELEGACJE 2026-09-12] Tabela pozycji: „wypełnione" = co najmniej jeden wiersz.
                else if (f.type === FIELD_TYPE.REPEATER) wypelnione = Array.isArray(val) && val.length > 0;
                // [polecenie właściciela 2026-09-30, A] ŚWIADOMIE: jawne „Bez osoby zastępującej” (znacznik
                // utils/hrBrakOsoby.BRAK_ZASTEPCY — obiekt, nie null) SPEŁNIA wymóg pola obowiązkowego.
                // „Jak mam, że nie mam zastępcy, to go nie mam” — deklaracja braku to odpowiedź, nie luka.
                else wypelnione = val !== undefined && val !== "" && val !== null;
                if (!wypelnione) bledy[f.name] = komunikatPolaWymaganego(f);
            });
        return bledy;
    }, [activeType, formData, dateValidationError, balanceInsufficient, mustPickCompany, companyId]);
    const isFormValid = !!activeType && Object.keys(bledyFormularza).length === 0;

    const getBalanceLabel = (limitPool) => {
        if (!limitPool) return null;
        const info = balancePoolCache[limitPool];
        const balance = balances[limitPool];
        if (!info || !balance) return null;
        const remaining = balance.total - balance.used - balance.pending;
        return `${remaining} z ${balance.total} ${info.unit}`;
    };

    // Load balance pool info for category types
    useEffect(() => {
        const loadPoolInfos = async () => {
            for (const t of categoryTypes) {
                if (t.limitPool && !balancePoolCache[t.limitPool]) {
                    await getBalancePoolCached(t.limitPool);
                }
            }
        };
        if (categoryTypes.length > 0) loadPoolInfos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryTypes]);

    // ── Balance summary for step 2 ──────────────────────────────
    const renderBalanceSummary = () => {
        if (!activeType?.limitPool) return null;
        const info = activeBalancePoolInfo;
        const balance = balances[activeType.limitPool];
        if (!info || !balance) return null;
        const remaining = balance.total - balance.used - balance.pending;
        const requestDays = formData.workDays || (formData.date ? 1 : 0);
        const afterRequest = remaining - requestDays;

        return (
            <div className={`${styles.balanceSummary} ${afterRequest < 0 ? styles.balanceSummaryDanger : ""}`}>
                <div className={styles.balanceSummaryTitle}>{info.label}</div>
                <div className={styles.balanceSummaryRow}>
                    <span>Dostępne:</span>
                    <strong style={{ color: remaining <= 0 ? "#EF4444" : "#10B981" }}>
                        {remaining} {info.unit}
                    </strong>
                </div>
                {requestDays > 0 && (
                    <div className={styles.balanceSummaryRow}>
                        <span>Po złożeniu:</span>
                        <strong style={{ color: afterRequest < 0 ? "#EF4444" : "#10B981" }}>
                            {afterRequest} {info.unit}
                        </strong>
                    </div>
                )}
                {balance.pending > 0 && (
                    <div className={styles.balanceSummaryRow}>
                        <span>Oczekujące:</span>
                        <span className={styles.balancePendingText}>{balance.pending} {info.unit}</span>
                    </div>
                )}
                {afterRequest < 0 && (
                    <div className={styles.balanceWarning}>
                        Niewystarczające saldo — nie można złożyć wniosku
                    </div>
                )}
            </div>
        );
    };

    // ── Withdrawal Step 2: request picker + reason ─────────────
    const renderWithdrawalStep2 = () => {
        if (!activeType) return null;

        // Phase 1: No original selected — show request picker
        if (!selectedOriginal) {
            return (
                <motion.div key="step2-withdrawal-picker" variants={slideVariants} initial="enter" animate="center" exit="exit">
                    <Informator id="hr.wycofanie-wniosku">
                        Wybierz wniosek, który chcesz wycofać. Dostępne są tylko wnioski zrealizowane w ciągu ostatnich 14 dni,
                        które nie mają jeszcze aktywnego wniosku o wycofanie.
                    </Informator>

                    {pokazBledyPol && bledyFormularza.__wycofanie && (
                        <small className={styles.fieldError} role="alert">{bledyFormularza.__wycofanie}</small>
                    )}

                    {eligibleLoading && (
                        <div className={styles.withdrawalLoading}>
                            <FiLoader size={16} style={{ animation: "spin 1s linear infinite" }} />
                            <span>Ładowanie wniosków…</span>
                        </div>
                    )}

                    {!eligibleLoading && eligibleRequests.length === 0 && (
                        <div className={styles.withdrawalEmpty}>
                            <FiInbox size={36} className={styles.withdrawalEmptyIcon} />
                            <p className={styles.withdrawalEmptyText}>
                                Brak wniosków kwalifikujących się do wycofania. Wycofanie jest dostępne tylko
                                dla wniosków zrealizowanych w ciągu ostatnich 14 dni.
                            </p>
                        </div>
                    )}

                    {!eligibleLoading && eligibleRequests.length > 0 && (
                        <div
                            className={styles.withdrawalList}
                            data-pole-z-bledem={pokazBledyPol && bledyFormularza.__wycofanie ? "__wycofanie" : undefined}
                        >
                            {eligibleRequests.map((req) => {
                                const fd = req.formData || {};
                                const dateFrom = fd.dateFrom || fd.date;
                                const dateTo = fd.dateTo || dateFrom;
                                const dateLabel = dateFrom && dateTo && dateFrom !== dateTo
                                    ? `${new Date(dateFrom + "T00:00:00").toLocaleDateString("pl-PL")} – ${new Date(dateTo + "T00:00:00").toLocaleDateString("pl-PL")}`
                                    : dateFrom ? new Date(dateFrom + "T00:00:00").toLocaleDateString("pl-PL") : "";
                                return (
                                    <button
                                        key={req.id}
                                        className={styles.withdrawalItem}
                                        /* [okno, poprawka 2026-09-28] Wybór wniosku do wycofania to już wypełnianie formularza —
                                           ✕ / Anuluj / „Wróć do listy” mają zapytać przed porzuceniem (jak po wpisaniu powodu). */
                                        onClick={() => { handleSelectOriginal(req); setEdytowano(true); }}
                                    >
                                        <div className={styles.withdrawalItemLeft}>
                                            <span className={styles.withdrawalItemNumber}>{req.requestNumber}</span>
                                            <span className={styles.withdrawalItemType}>{req._typeLabel}</span>
                                        </div>
                                        <div className={styles.withdrawalItemRight}>
                                            {dateLabel && <span className={styles.withdrawalItemDate}>{dateLabel}</span>}
                                            <FiChevronRight size={16} className={styles.typeCardArrow} />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </motion.div>
            );
        }

        // Phase 2: Original selected — show card + reason textarea
        return (
            <motion.div key="step2-withdrawal-form" variants={slideVariants} initial="enter" animate="center" exit="exit">
                <div className={styles.formSection}>
                    <div className={styles.withdrawalSelected}>
                        <div className={styles.withdrawalSelectedHeader}>
                            <span className={styles.withdrawalSelectedTitle}>Wniosek do wycofania</span>
                            <button
                                className={styles.withdrawalChangeBtn}
                                onClick={() => { setSelectedOriginal(null); setFormData({}); }}
                            >
                                <FiRotateCcw size={11} /> Zmień
                            </button>
                        </div>
                        <div className={styles.withdrawalSelectedCard}>
                            <div className={styles.withdrawalSelectedRow}>
                                <span>Numer:</span>
                                <span>{formData.originalRequestNumber}</span>
                            </div>
                            <div className={styles.withdrawalSelectedRow}>
                                <span>Typ:</span>
                                <span>{formData.originalTypeLabel}</span>
                            </div>
                            <div className={styles.withdrawalSelectedRow}>
                                <span>Okres:</span>
                                <span>{formData.originalDateRange}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.fieldGroup} data-pole-z-bledem={pokazBledyPol && bledyFormularza.reason ? "reason" : undefined}>
                        <label className={styles.fieldLabel}>
                            Powód wycofania
                            <span className={styles.fieldRequired}>*</span>
                        </label>
                        <textarea
                            className={styles.fieldTextarea}
                            value={formData.reason || ""}
                            onChange={(e) => handleFieldChange("reason", e.target.value)}
                            placeholder="Podaj powód wycofania wniosku..."
                            rows={4}
                        />
                        {pokazBledyPol && bledyFormularza.reason && (
                            <small className={styles.fieldError} role="alert">{bledyFormularza.reason}</small>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    };

    // ── Step 0: Category selection ───────────────────────────────
    const renderStep0 = () => {
        const q = typeSearch.trim().toLowerCase();
        const matches = q
            ? allTypes.filter((t) =>
                (t.label || "").toLowerCase().includes(q) ||
                (t.description || "").toLowerCase().includes(q) ||
                (t._catLabel || "").toLowerCase().includes(q))
            : [];
        return (
        <motion.div key="step0" variants={slideVariants} initial="enter" animate="center" exit="exit">
            {/* Wyszukiwarka typów — pracownika nie interesuje pion (HR/Kadry), tylko „co chce złożyć" */}
            <div className={styles.typeSearchBox}>
                <FiSearch size={16} className={styles.typeSearchIcon} />
                <input
                    className={styles.typeSearchInput}
                    placeholder="Szukaj wniosku (np. urlop, PPK, zaświadczenie)…"
                    value={typeSearch}
                    onChange={(e) => setTypeSearch(e.target.value)}
                    autoFocus
                />
            </div>

            {/* Gospodarz umie przejąć wniosek zarządczy → mówimy wprost, gdzie są wnioski o pracownikach,
                i dajemy tam przejście jednym kliknięciem, zamiast kazać szukać drugiego przycisku. */}
            {onWniosekZarzadczy && (
                <div className={`${styles.infoBox} ${styles.infoBoxRow}`}>
                    <FiUsers size={15} className={styles.infoBoxIcon} />
                    <span>
                        Tu składasz wnioski <strong>dotyczące Ciebie</strong>. Wniosek dotyczący pracownika
                        Twojego działu (ocena, zmiana stanowiska, zatrudnienie) złożysz przez{" "}
                        <button type="button" className={styles.linkBtn} onClick={() => onWniosekZarzadczy(null)}>
                            Wniosek w imieniu pracownika
                        </button>.
                    </span>
                </div>
            )}

            {categories !== null && categories.length === 0 && (
                /* [brief pkt 6 + W-1] Po zawężeniu może nie zostać nic — wtedy BIAŁA KARTA
                   z wyjaśnieniem, a nie pusta lista bez słowa. Awarię API mówimy wprost,
                   bo to zupełnie co innego niż „nic Ci nie przysługuje". */
                <div className={styles.emptyCard}>
                    {typesError ? <FiAlertCircle size={32} className={styles.emptyCardIcon} />
                        : <FiInbox size={32} className={styles.emptyCardIcon} />}
                    <h3 className={styles.emptyCardTitle}>
                        {typesError ? "Nie udało się pobrać listy wniosków" : "Nie ma wniosków, które możesz złożyć"}
                    </h3>
                    <p className={styles.emptyCardText}>
                        {typesError
                            ? "Spróbuj ponownie za chwilę. Jeżeli to się powtarza, zgłoś problem do działu IT."
                            : "Zestaw wniosków zależy od Twojej umowy, wymiaru etatu i przyznanych pul (np. urlopowej). Jeżeli czegoś tu brakuje, skontaktuj się z działem kadr."}
                    </p>
                </div>
            )}

            {categories !== null && categories.length > 0 && (q ? (
                <div className={styles.typeList}>
                    {matches.length === 0 && (
                        <div className={styles.searchEmpty}>Brak wniosków pasujących do „{typeSearch}".</div>
                    )}
                    {matches.map((t) => {
                        const TypeIcon = t.icon;
                        return (
                            <button
                                key={t.id}
                                className={styles.typeCard}
                                onClick={() => handleTypeSelect(t.id)}
                            >
                                <div className={styles.typeCardIcon} style={{ background: t._catBg, color: t._catColor }}>
                                    {TypeIcon ? <TypeIcon size={17} /> : null}
                                </div>
                                <div className={styles.typeCardInfo}>
                                    <h4 className={styles.typeCardLabel}>{t.label}</h4>
                                    <span className={styles.typeCardMeta}>{t._catLabel}</span>
                                </div>
                                <FiChevronRight size={16} className={styles.typeCardArrow} />
                            </button>
                        );
                    })}
                </div>
            ) : (
            <div className={styles.categoryGrid}>
                {categories.map((cat) => {
                    const CatIcon = cat.icon;
                    return (
                        <button
                            key={cat.id}
                            className={styles.categoryCard}
                            onClick={() => handleCategorySelect(cat.id)}
                        >
                            <div className={styles.categoryCardIcon} style={{ background: cat.bg, color: cat.color }}>
                                {CatIcon ? <CatIcon size={20} /> : null}
                            </div>
                            <h3 className={styles.categoryCardTitle}>{cat.label}</h3>
                            {/* [polecenie właściciela 2026-09-29] „prosiłem by był opis, jakie wnioski dotyczą danego kafelka” — kategoria nie ma
                                w bazie opisu, więc kafel wymienia wnioski w środku (to samo źródło co okno „Utwórz nowe zgłoszenie”). */}
                            <p className={styles.categoryCardDesc} data-testid="opis-kategorii-wnioskow">{cat.description || opisKategoriiWnioskow(cat, 6)}</p>
                            <span className={styles.categoryCardCount}>{cat._typesCount} {odmiana(cat._typesCount, ["typ wniosku", "typy wniosków", "typów wniosków"])}</span>
                        </button>
                    );
                })}
            </div>
            ))}
        </motion.div>
        );
    };

    // ── Step 1: wybór typu ───────────────────────────────────────
    // Bez wyszarzania i bez „powodu niedostępności": lista przychodzi z serwera już
    // zawężona, więc każda pozycja tutaj jest wnioskiem, który wolno złożyć (brief pkt 6).
    const renderStep1 = () => (
        <motion.div key="step1" variants={slideVariants} initial="enter" animate="center" exit="exit">
            <div className={styles.typeList}>
                {categoryTypes.map((t) => {
                    const TypeIcon = t.icon;
                    const balanceLabel = getBalanceLabel(t.limitPool);
                    return (
                        <button
                            key={t.id}
                            className={styles.typeCard}
                            onClick={() => handleTypeSelect(t.id)}
                        >
                            <div className={styles.typeCardIcon} style={{ background: activeCat?.bg, color: activeCat?.color }}>
                                {TypeIcon ? <TypeIcon size={17} /> : null}
                            </div>
                            <div className={styles.typeCardInfo}>
                                <h4 className={styles.typeCardLabel}>{t.label}</h4>
                                <span className={styles.typeCardMeta}>{t.description || opisTypuWniosku(t, activeCat?.label).co}</span>
                            </div>
                            {balanceLabel && (
                                <span className={styles.typeCardLimit} style={{ background: activeCat?.bg, color: activeCat?.color }}>
                                    {balanceLabel}
                                </span>
                            )}
                            <FiChevronRight size={16} className={styles.typeCardArrow} />
                        </button>
                    );
                })}
            </div>
        </motion.div>
    );

    // ── Step 2 dla typu ZARZĄDCZEGO: nie formularz, tylko wskazanie właściwych drzwi ──────
    const renderTypZarzadczy = () => (
        <motion.div key="step2-zarzadczy" variants={slideVariants} initial="enter" animate="center" exit="exit">
            <div className={styles.emptyCard} data-testid="wniosek-zarzadczy-blokada">
                <FiUsers size={32} className={styles.emptyCardIcon} />
                <h3 className={styles.emptyCardTitle}>Ten wniosek dotyczy pracownika, nie Ciebie</h3>
                <p className={styles.emptyCardText}>
                    „{activeType?.label}” składa się w imieniu pracownika — najpierw wskazuje się osobę
                    z Twojego działu, potem wypełnia formularz. Tutaj, w trybie „w swoim imieniu”,
                    nie ma jak wskazać pracownika, więc wniosek nie zostanie złożony.
                </p>
                {onWniosekZarzadczy ? (
                    <button
                        type="button"
                        className={styles.submitBtn}
                        onClick={() => onWniosekZarzadczy(activeType?.code || selectedType)}
                    >
                        <FiUsers size={14} /> Przejdź do wniosku w imieniu pracownika
                    </button>
                ) : (
                    <p className={styles.emptyCardText}>
                        Wejdź w <strong>Mój Dział → Wnioski</strong> i użyj przycisku „Wniosek w imieniu pracownika”.
                    </p>
                )}
            </div>
        </motion.div>
    );

    // [okno] Błąd POD polem (po „Dalej” w oknie „Utwórz nowe zgłoszenie”). W szufladzie funkcja oddaje DOKŁADNIE ten
    // element, który narysowało pole (ten sam kształt, ten sam klucz) — /wnioski-hr i Mój Dział bez zmian.
    // [poprawka po przeglądzie 2026-09-28] W oknie pole stoi ZAWSZE w tej samej obwolucie, a błąd dokłada wyłącznie
    // atrybut i <small>. Pierwsza wersja owijała pole tylko wtedy, gdy miało błąd: po pierwszej wpisanej literze błąd
    // gasł, drzewo zmieniało kształt i React montował NOWY <input> — kursor uciekał z pola, kolejne litery szły
    // w próżnię, a pola z komponentem (osoba z działu, data) traciły swój stan.
    const zBledemPola = (rysuj) => (field, i) => {
        const el = rysuj(field);
        if (!wOknie || !el) return el;
        const blad = pokazBledyPol && field?.name ? bledyFormularza[field.name] : null;
        return (
            <div key={field?.name || `pole-${i}`} className={styles.poleZBledem} data-pole-z-bledem={blad ? field.name : undefined}>
                {el}
                {blad && <small className={styles.fieldError} role="alert">{blad}</small>}
            </div>
        );
    };

    // ── Step 2: Dynamic form ─────────────────────────────────────
    const renderStep2 = () => {
        if (!activeType) return null;

        return (
            <motion.div key="step2" variants={slideVariants} initial="enter" animate="center" exit="exit">
                <div className={styles.formSection}>
                    {/* ── Spółka, DO KTÓREJ składam wniosek (brief 2026-08-30, pkt 3) ──
                        Logo i dane spółki na dokumencie biorą się właśnie stąd, a serwer zamraża
                        je w snapshocie wniosku. Przy jednej spółce nie ma o co pytać — mówimy
                        tylko, dokąd pismo idzie. */}
                    {companies.length > 0 && (
                        mustPickCompany ? (
                            <div className={styles.fieldGroup} data-pole-z-bledem={pokazBledyPol && bledyFormularza.__spolka ? "__spolka" : undefined}>
                                <label className={styles.fieldLabel}>
                                    Spółka, do której składasz wniosek
                                    <span className={styles.fieldRequired}>*</span>
                                </label>
                                <small className={styles.fieldHint}>
                                    Jesteś przypisany do kilku spółek. Wskaż tę, do której kierujesz to pismo —
                                    jej dane i logo trafią na dokument.
                                </small>
                                <select
                                    className={styles.fieldSelect}
                                    value={companyId ?? ""}
                                    onChange={(e) => { setCompanyId(e.target.value ? Number(e.target.value) : null); setEdytowano(true); }}
                                >
                                    <option value="">Wybierz spółkę...</option>
                                    {companies.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}{c.isPrimary ? " (podstawowa)" : ""}</option>
                                    ))}
                                </select>
                                {pokazBledyPol && bledyFormularza.__spolka && (
                                    <small className={styles.fieldError} role="alert">{bledyFormularza.__spolka}</small>
                                )}
                            </div>
                        ) : (
                            <div className={styles.companyNote}>
                                <FiBriefcase size={14} />
                                <span>Wniosek kierujesz do: <strong>{activeCompany?.name || companies[0].name}</strong></span>
                            </div>
                        )
                    )}

                    {/* Last request info */}
                    {lastRequest && (
                        <div className={styles.lastRequestInfo}>
                            <span className={styles.lastRequestLabel}>Ostatni wniosek tego typu:</span>
                            <span className={styles.lastRequestDate}>
                                {new Date(lastRequest.submittedAt).toLocaleDateString("pl-PL")}
                            </span>
                            <span
                                className={styles.lastRequestStatus}
                                style={{
                                    background: getStatusInfoFn(lastRequest.status)?.bg || "rgba(107,114,128,0.08)",
                                    color: getStatusInfoFn(lastRequest.status)?.color || "#6B7280",
                                }}
                            >
                                {getStatusInfoFn(lastRequest.status)?.label || lastRequest.status}
                            </span>
                        </div>
                    )}
                    {renderBalanceSummary()}

                    {dateValidationError && (
                        <div className={styles.dateError}>
                            {dateValidationError}
                        </div>
                    )}

                    {activeType.fields.map(zBledemPola((field) => {
                        if (field.type === FIELD_TYPE.HIDDEN) return null;
                        // [R24] showIf + showIfValue: pola pojazdu prywatnego tylko przy srodekTransportu === "prywatny".
                        if (!isFieldVisible(field, formData)) return null;

                        // [DELEGACJE 2026-09-12] Trzy nowe typy rysuje WSPÓLNY renderer (utils/hrFormFieldRenderer) —
                        // ten sam, którego używa edycja wniosku w szufladzie szczegółów. Trzecia kopia gałęzi
                        // rozjechałaby się przy pierwszej zmianie (lekcja „dateRange": typ bez gałęzi spadał
                        // do zwykłego <input>, a wartość nie zasilała kluczy).
                        if (TYPY_ROZLICZEN.has(field.type)) {
                            return renderFormField(field, formData, handleFieldChange, { user });
                        }

                        if (field.type === FIELD_TYPE.INFO) {
                            return <div key={field.name || "info"} className={styles.infoBox}>{field.content}</div>;
                        }

                        if (field.type === FIELD_TYPE.CHECKBOX) {
                            return (
                                <div key={field.name} className={styles.checkboxRow}>
                                    <input
                                        type="checkbox"
                                        className={styles.checkboxInput}
                                        checked={!!formData[field.name]}
                                        onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                                        id={`field-${field.name}`}
                                    />
                                    <label className={styles.checkboxLabel} htmlFor={`field-${field.name}`}>
                                        {field.label}
                                    </label>
                                </div>
                            );
                        }

                        if (field.type === FIELD_TYPE.SELECT) {
                            return (
                                <div key={field.name} className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        {field.label}
                                        {field.required && <span className={styles.fieldRequired}>*</span>}
                                    </label>
                                    {field.description && <small className={styles.fieldHint}>{field.description}</small>}
                                    <select
                                        className={styles.fieldSelect}
                                        value={formData[field.name] || ""}
                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                    >
                                        <option value="">Wybierz...</option>
                                        {(field.options || []).map((opt, oi) => {
                                            const o = typeof opt === "string" ? { value: opt, label: opt } : opt; // tolerancja starych zapisów
                                            return <option key={o.value ?? oi} value={o.value}>{o.label ?? o.value}</option>;
                                        })}
                                    </select>
                                </div>
                            );
                        }

                        if (field.type === FIELD_TYPE.TEXTAREA) {
                            return (
                                <div key={field.name} className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        {field.label}
                                        {field.required && <span className={styles.fieldRequired}>*</span>}
                                    </label>
                                    {field.description && <small className={styles.fieldHint}>{field.description}</small>}
                                    <textarea
                                        className={styles.fieldTextarea}
                                        value={formData[field.name] || ""}
                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                        placeholder={field.placeholder || `Wpisz ${field.label.toLowerCase()}...`}
                                    />
                                </div>
                            );
                        }

                        if (field.type === FIELD_TYPE.DATE) {
                            return (
                                <div key={field.name} className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        {field.label}
                                        {field.required && <span className={styles.fieldRequired}>*</span>}
                                    </label>
                                    <DatePicker
                                        value={formData[field.name] || ""}
                                        onChange={(val) => handleFieldChange(field.name, val)}
                                        placeholder="Wybierz datę"
                                        clearable={!field.required}
                                        ariaLabel={field.label}
                                        min={minPolaDaty(field.name, activeType, nazwaPolaOd(field.name) ? minDlaDo(formData[nazwaPolaOd(field.name)]) : undefined)}
                                    />
                                </div>
                            );
                        }

                        if (field.type === FIELD_TYPE.TIME) {
                            return (
                                <div key={field.name} className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        {field.label}
                                        {field.required && <span className={styles.fieldRequired}>*</span>}
                                    </label>
                                    <TimePicker
                                        value={formData[field.name] || ""}
                                        onChange={(val) => handleFieldChange(field.name, val)}
                                        placeholder="Wybierz godzinę"
                                        clearable={!field.required}
                                        ariaLabel={field.label}
                                    />
                                </div>
                            );
                        }

                        if (field.type === FIELD_TYPE.NUMBER) {
                            return (
                                <div key={field.name} className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        {field.label}
                                        {field.required && !field.computed && <span className={styles.fieldRequired}>*</span>}
                                        {/* [brief pkt 2/6] Pole pochodne (np. suma dni od-do) liczy SERWER
                                            i ignoruje to, co przyśle przeglądarka — mówimy to wprost. */}
                                        {field.computed && (
                                            <span className={styles.computedBadge}><FiCpu size={10} /> liczy system</span>
                                        )}
                                    </label>
                                    <input
                                        type="number"
                                        className={`${styles.fieldInput} ${field.readonly ? styles.fieldInputReadonly : ""}`}
                                        value={formData[field.name] ?? ""}
                                        onChange={(e) => handleFieldChange(field.name, e.target.value ? Number(e.target.value) : "")}
                                        readOnly={field.readonly}
                                        min={0}
                                        placeholder={field.readonly ? "(wyliczane automatycznie)" : "0"}
                                    />
                                </div>
                            );
                        }

                        if (field.type === FIELD_TYPE.FILE) {
                            return (
                                <div key={field.name} className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        {field.label}
                                        {field.required && <span className={styles.fieldRequired}>*</span>}
                                    </label>
                                    <label className={styles.fileLabel}>
                                        <FiUpload size={14} />
                                        <span>{formData[field.name]?.name || "Wybierz plik..."}</span>
                                        <input
                                            type="file"
                                            className={styles.fileInput}
                                            onChange={(e) => handleFieldChange(field.name, e.target.files?.[0] || null)}
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        />
                                    </label>
                                </div>
                            );
                        }

                        if (field.type === FIELD_TYPE.USER_PICKER) {
                            return (
                                <div key={field.name} className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        {field.label}
                                        {field.required && <span className={styles.fieldRequired}>*</span>}
                                    </label>
                                    {/* [polecenie właściciela 2026-09-30, A] Tryb wniosku: cały dział składającego
                                        (z komórkami) z serwera, bez wpisu ręcznego; „Bez osoby zastępującej” przy
                                        polach zastępstwa. Kreator składa wnioski o sobie — bez forUserId. */}
                                    <DepartmentMemberPicker
                                        value={formData[field.name] || null}
                                        onChange={(val) => handleFieldChange(field.name, val)}
                                        excludeUserId={user?.id}
                                        placeholder="Wybierz osobę z działu..."
                                        hrTypeCode={activeType.code || selectedType}
                                        fieldName={field.name}
                                        pozwolBrak={czyPoleZastepstwa(field)}
                                    />
                                </div>
                            );
                        }

                        return (
                            <div key={field.name} className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    {field.label}
                                    {field.required && <span className={styles.fieldRequired}>*</span>}
                                </label>
                                {field.description && <small className={styles.fieldHint}>{field.description}</small>}
                                <input
                                    type="text"
                                    className={styles.fieldInput}
                                    value={formData[field.name] || ""}
                                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                    placeholder={
                                        field.placeholder ||
                                        (field.type === FIELD_TYPE.IBAN ? "PL 00 0000 0000 0000 0000 0000 0000" : "")
                                    }
                                />
                                {sensitiveHints[field.name] && (
                                    <small className={styles.fieldHint} style={{ color: "#6366F1" }}>
                                        🔒 W systemie zapisano: {sensitiveHints[field.name]} — ze względu na ochronę danych wpisz wartość ponownie.
                                    </small>
                                )}
                            </div>
                        );
                    }))}
                </div>
            </motion.div>
        );
    };

    // ══ OPRAWA „OKNO” — karta „Wnioski pracownicze” w oknie „Utwórz nowe zgłoszenie” (polecenie właściciela 2026-09-28) ══
    // Rama jest KOPIĄ UKŁADU formularza zgłoszenia (nagłówek z „Wróć”, przewijana treść, stopka z przyciskami) na klasach
    // gospodarza (`klasyOkna`), a treść formularza to te same funkcje rysujące co w szufladzie. Zamyka Modal gospodarza.
    const kategoriaTypu = activeType
        ? (categories || []).find((c) => (c.types || []).some((t) => t.id === activeType.id || t.code === activeType.code)) || null
        : null;

    const wrocDoListyOkna = () => { if (typeof onWstecz === "function") onWstecz(); };
    const krokWsteczOkna = () => {
        if (krokOkna === "podsumowanie") { setKrokOkna("formularz"); return; }
        wrocDoListyOkna();
    };
    const dalejOkna = () => {
        if (!activeType) return;
        if (!isFormValid) {
            setPokazBledyPol(true);
            setProbaDalej((n) => n + 1);
            return;
        }
        setPokazBledyPol(false);
        setKrokOkna("podsumowanie");
    };

    const renderPodsumowanie = () => {
        const wycofanie = activeType?.code === WITHDRAWAL_TYPE_CODE;
        const dane = bezUkrytychPol(activeType?.fields || [], formData) || {};
        const wiersze = wycofanie
            ? [
                { klucz: "originalRequestNumber", etykieta: "Wniosek do wycofania",
                  wartosc: [formData.originalRequestNumber, formData.originalTypeLabel].filter(Boolean).join(" · ") },
                { klucz: "originalDateRange", etykieta: "Okres", wartosc: formData.originalDateRange || "" },
                { klucz: "reason", etykieta: "Powód wycofania", wartosc: formData.reason || "" },
            ]
            : (activeType?.fields || [])
                .filter((f) => f && f.name && f.type !== FIELD_TYPE.INFO && f.type !== FIELD_TYPE.HIDDEN && isFieldVisible(f, formData))
                .map((f) => ({ klucz: f.name, etykieta: f.label || f.name, wartosc: wartoscPolaDoPodsumowania(f, dane[f.name], dane) }));
        const wypelnione = wiersze.filter((w) => w.wartosc !== "");
        return (
            <motion.div key="step-podsumowanie" variants={slideVariants} initial="enter" animate="center" exit="exit">
                <div className={styles.podsumowanie} data-testid="podsumowanie-wniosku">
                    <h5 className={styles.podsumowanieTytul}>Sprawdź wniosek przed wysłaniem</h5>
                    <dl className={styles.podsumowanieLista}>
                        <div className={styles.podsumowanieWiersz}>
                            <dt>Wniosek</dt>
                            <dd>{activeType?.label}</dd>
                        </div>
                        {companies.length > 0 && (
                            <div className={styles.podsumowanieWiersz}>
                                <dt>Spółka</dt>
                                <dd>{activeCompany?.name || companies[0].name}</dd>
                            </div>
                        )}
                        {/* [poprawka 2026-09-28] Ścieżki akceptacji lista typów nie niesie (serwer: „kuchnia kadr”) — nie zgadujemy,
                            tylko mówimy, gdzie ją zobaczyć. Prawdziwe kroki pokazuje potwierdzenie po wysłaniu. */}
                        <div className={styles.podsumowanieWiersz} data-testid="podsumowanie-kto-akceptuje">
                            <dt>Kto akceptuje</dt>
                            <dd>{KTO_AKCEPTUJE_PRZED_ZLOZENIEM}</dd>
                        </div>
                        {wypelnione.map((w) => (
                            <div key={w.klucz} className={styles.podsumowanieWiersz}>
                                <dt>{w.etykieta}</dt>
                                <dd>{w.wartosc}</dd>
                            </div>
                        ))}
                    </dl>
                    {!wycofanie && wypelnione.length === 0 && (
                        <p className={styles.fieldHint}>Ten wniosek nie ma pól do wypełnienia — wystarczy go wysłać.</p>
                    )}
                    <p className={styles.podsumowanieUwaga}>
                        Po wysłaniu okno pokaże numer wniosku i to, co dzieje się z nim dalej; status i historię zobaczysz
                        w „Wnioski HR”. Żeby coś poprawić, kliknij „Wstecz”.
                    </p>
                </div>
            </motion.div>
        );
    };

    const renderOkno = () => {
        if (!isOpen) return null;
        const k = klasyOkna || {};
        const naPodsumowaniu = krokOkna === "podsumowanie";
        const IkonaTypu = activeType?.icon || null;
        const krokAktywny = naPodsumowaniu ? 3 : 2;
        const liczbaBledow = Object.keys(bledyFormularza).length;
        const wycofanie = activeType?.code === WITHDRAWAL_TYPE_CODE;
        return (
            <div ref={oknoRef} className={k.widok || styles.okno} data-testid="kreator-wniosku-w-oknie"
                data-krok-okna={naPodsumowaniu ? "podsumowanie" : "formularz"}>
                <div className={k.naglowek || styles.oknoNaglowek}>
                    <button type="button" className={k.wstecz || styles.backBtn} onClick={wrocDoListyOkna}>
                        <FiArrowLeft size={14} /> Wróć do listy
                    </button>
                    <div className={k.info || styles.oknoNaglowekInfo}>
                        <div
                            className={k.ikona || styles.oknoIkona}
                            style={kategoriaTypu?.bg ? { background: kategoriaTypu.bg, color: kategoriaTypu.color } : undefined}
                            aria-hidden="true"
                        >
                            {IkonaTypu ? <IkonaTypu size={16} /> : <FiSend size={15} />}
                        </div>
                        <div className={styles.oknoTytulBlok}>
                            <h4 className={k.tytul || styles.drawerTitle}>{activeType?.label || "Wniosek"}</h4>
                            <p className={styles.drawerSubtitle}>
                                {naPodsumowaniu ? "Sprawdź dane i wyślij wniosek"
                                    : typZarzadczyWybrany ? "Ten wniosek składa się w imieniu pracownika"
                                    : "Wypełnij formularz — pola z * są obowiązkowe"}
                            </p>
                        </div>
                    </div>
                </div>

                <div className={styles.oknoKroki} aria-label="Postęp składania wniosku">
                    {KROKI_OKNA.map((label, i) => (
                        <React.Fragment key={label}>
                            {i > 0 && <div className={styles.stepConnector} />}
                            <div
                                className={`${styles.step} ${i === krokAktywny ? styles.stepActive : ""} ${i < krokAktywny ? styles.stepDone : ""}`}
                                aria-current={i === krokAktywny ? "step" : undefined}
                            >
                                <span className={styles.stepNumber}>{i < krokAktywny ? <FiCheck size={12} /> : i + 1}</span>
                                <span className={styles.oknoKrokEtykieta}>{label}</span>
                            </div>
                        </React.Fragment>
                    ))}
                </div>

                <div className={k.tresc || styles.drawerBody}>
                    {!activeType ? (
                        <LadowanieStrony wariant="sekcja" etykieta="Wczytywanie formularza wniosku…" bezPaska />
                    ) : (
                        <>
                            {pokazBledyPol && liczbaBledow > 0 && !naPodsumowaniu && (
                                <div className={styles.bledyWniosku} role="alert" data-testid="bledy-wniosku">
                                    <FiAlertCircle size={15} aria-hidden="true" />
                                    <span>
                                        Nie można przejść dalej — popraw {liczbaBledow}{" "}
                                        {odmiana(liczbaBledow, ["pozycję", "pozycje", "pozycji"])} oznaczone poniżej.
                                    </span>
                                </div>
                            )}
                            {naPodsumowaniu ? renderPodsumowanie()
                                : typZarzadczyWybrany ? renderTypZarzadczy()
                                : wycofanie ? renderWithdrawalStep2()
                                : renderStep2()}
                        </>
                    )}
                </div>

                {!typZarzadczyWybrany && (
                    <div className={k.stopka || styles.drawerFooter}>
                        <button type="button" className={k.anuluj || styles.cancelBtn} onClick={() => { if (typeof onClose === "function") onClose(); }}>
                            Anuluj
                        </button>
                        <button type="button" className={k.anuluj || styles.cancelBtn} onClick={krokWsteczOkna}>
                            Wstecz
                        </button>
                        {naPodsumowaniu ? (
                            <button
                                type="button"
                                className={k.glowny || styles.submitBtn}
                                onClick={handleSubmit}
                                disabled={!isFormValid || submitting}
                            >
                                <FiSend size={15} />
                                {submitting ? "Wysyłanie..." : "Wyślij wniosek"}
                            </button>
                        ) : (
                            <button type="button" className={k.glowny || styles.submitBtn} onClick={dalejOkna} disabled={!activeType}>
                                Dalej <FiChevronRight size={15} />
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (wOknie) return renderOkno();

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className={styles.overlay}
                        variants={overlayVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={nudgeDrawer}
                    />
                    <motion.div
                        className={styles.drawer}
                        variants={drawerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <div className={styles.drawerHeader}>
                            <div className={styles.drawerHeaderLeft}>
                                {step > 0 && (
                                    <button className={styles.backBtn} onClick={handleBack} title="Wstecz">
                                        <FiArrowLeft size={15} />
                                    </button>
                                )}
                                <div>
                                    <h2 className={styles.drawerTitle}>
                                        {step === 0 ? "Nowy wniosek" :
                                         step === 1 ? activeCat?.label :
                                         activeType?.label}
                                    </h2>
                                    <p className={styles.drawerSubtitle}>
                                        {step === 0 ? "Wniosek w swoim imieniu — wybierz kategorię" :
                                         step === 1 ? "Wybierz typ wniosku" :
                                         typZarzadczyWybrany ? "Ten wniosek składa się w imieniu pracownika" :
                                         "Wypełnij formularz i złóż wniosek"}
                                    </p>
                                </div>
                            </div>
                            <button className={styles.closeBtn} onClick={handleClose} title="Zamknij">
                                <FiX size={16} />
                            </button>
                        </div>

                        <div className={styles.steps}>
                            {STEP_LABELS.map((label, i) => (
                                <React.Fragment key={label}>
                                    {i > 0 && <div className={styles.stepConnector} />}
                                    <div className={`${styles.step} ${i === step ? styles.stepActive : ""} ${i < step ? styles.stepDone : ""}`}>
                                        <span className={styles.stepNumber}>
                                            {i < step ? <FiCheck size={12} /> : i + 1}
                                        </span>
                                        <span>{label}</span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>

                        <div className={styles.drawerBody}>
                            <AnimatePresence mode="wait">
                                {step === 0 && renderStep0()}
                                {step === 1 && renderStep1()}
                                {step === 2 && (
                                    typZarzadczyWybrany ? renderTypZarzadczy()
                                        : activeType?.code === WITHDRAWAL_TYPE_CODE
                                            ? renderWithdrawalStep2()
                                            : renderStep2()
                                )}
                            </AnimatePresence>
                        </div>

                        {step === 2 && !typZarzadczyWybrany && (
                            <div className={styles.drawerFooter}>
                                {activeType?.limitPool && (() => {
                                    const info = activeBalancePoolInfo;
                                    const balance = balances[activeType.limitPool];
                                    if (!info || !balance) return null;
                                    const remaining = balance.total - balance.used - balance.pending;
                                    const requestDays = formData.workDays || (formData.date ? 1 : 0);
                                    const afterRequest = remaining - requestDays;
                                    return (
                                        <div className={styles.footerBalance}>
                                            <span className={styles.footerBalanceLabel}>Zostało mi</span>
                                            <strong className={styles.footerBalanceValue} style={{ color: remaining <= 2 ? "#EF4444" : "#10B981" }}>
                                                {remaining} z {balance.total} {info.unit}
                                            </strong>
                                            {requestDays > 0 && (
                                                <span className={styles.footerBalanceAfter}>
                                                    (po złożeniu: {afterRequest})
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                                <div className={styles.footerActions}>
                                    <button className={styles.cancelBtn} onClick={handleClose}>
                                        Anuluj
                                    </button>
                                    <button
                                        className={styles.submitBtn}
                                        onClick={handleSubmit}
                                        disabled={!isFormValid || submitting}
                                    >
                                        <FiSend size={14} />
                                        {submitting ? "Składanie..." : "Złóż wniosek"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default HrNewRequestDrawer;
